import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Lightbulb, Link2, Layers, Wand2, Save, CalendarPlus, Loader2, ImagePlus, Radar, Maximize2, X, Check } from "lucide-react";
import ScheduleDialog from "@/components/ScheduleDialog";
import BrandTemplateStudio from "@/components/BrandTemplateStudio";
import { RecoveredVisuals } from "@/components/RecoveredVisuals";
import { ImageProgress } from "@/components/ImageProgress";

const sources = [
  { key: "idea", label: "Une idée", icon: Lightbulb, placeholder: "Décrivez l'idée, l'anecdote ou le message que vous voulez partager…" },
  { key: "link", label: "Un lien / article", icon: Link2, placeholder: "Collez le lien et/ou un résumé de l'article ou de la vidéo à commenter…" },
  { key: "pillar", label: "Un pilier", icon: Layers, placeholder: "Indiquez le pilier de contenu et l'angle souhaité…" },
];

const quickActions = ["Raccourcir", "Changer d'angle", "Ajouter une accroche plus forte", "Rendre plus personnel", "Ajouter un appel à l'action"];

// `embedded` : la page est montée dans l'onglet « Mode rapide » de « Créer un post ».
// Par défaut false → la route /generer continue de fonctionner exactement comme avant.
export default function Generator({ embedded = false }) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sourceType, setSourceType] = useState("idea");
  const [input, setInput] = useState("");
  const [threadId, setThreadId] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [busy, setBusy] = useState(false);
  const [iterating, setIterating] = useState(false);
  const [customInstr, setCustomInstr] = useState("");
  const [scheduleFor, setScheduleFor] = useState(null); // content string
  const [scheduleImage, setScheduleImage] = useState(null);
  const [studioFor, setStudioFor] = useState(null); // proposal content for the visual studio
  const [reusePrompt, setReusePrompt] = useState(null);
  const [scheduleMeta, setScheduleMeta] = useState(null);
  const [postImgBusy, setPostImgBusy] = useState(null);
  const [postImgJob, setPostImgJob] = useState(null);
  // Page quittée : on cesse d'interroger le serveur, pour que les visuels terminés restent
  // « non reçus » et s'affichent en haut de « Créer un post » au retour.
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [postImgResults, setPostImgResults] = useState({});
  const [postImgSel, setPostImgSel] = useState({});   // visuel sélectionné par proposition
  const [includePerson, setIncludePerson] = useState("non"); // Par défaut : affiche signature sans visage défini
  const [lightbox, setLightbox] = useState(null);     // visuel affiché en plein écran
  const [profile, setProfile] = useState(null);
  const prefillDone = useRef(false);

  useEffect(() => { api.get("/style-profile").then((r) => setProfile(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const rp = sessionStorage.getItem("reuse_prompt");
    if (rp) {
      sessionStorage.removeItem("reuse_prompt");
      setReusePrompt(rp);
      setStudioFor("");
    }
  }, []);

  const activeSource = sources.find((s) => s.key === sourceType);

  const pollJob = (jobId, onDone, onTick) =>
    new Promise((resolve, reject) => {
      const started = Date.now();
      const tick = async () => {
        if (!mounted.current) { reject(new Error("PAGE_QUITTEE")); return; }
        try {
          const { data } = await api.get(`/jobs/${jobId}`);
          if (data.status === "done") { onDone(data); resolve(data); return; }
          if (data.status === "error") { reject(new Error(data.error || "La génération a échoué.")); return; }
          if (onTick) onTick(data);
          if (Date.now() - started > 15 * 60 * 1000) { reject(new Error("Délai dépassé.")); return; }
          setTimeout(tick, 2000);
        } catch (e) { reject(e); }
      };
      tick();
    });

  const genPostImage = async (i, p) => {
    setPostImgBusy(i);
    setPostImgSel((prev) => ({ ...prev, [i]: null }));
    try {
      const aspect = "16:9"; // paysage 1536x1024, le format des affiches infographiques de référence
      const { data } = await api.post("/images/generate", { post_content: p, style: "post", aspect, include_person: includePerson });
      setPostImgJob(null);
      const res = await pollJob(data.job_id, () => {}, (d) => setPostImgJob(d));
      setPostImgResults((prev) => ({ ...prev, [i]: res.images || [] }));
      toast.success(`${res.images?.length || 0} visuel(s) généré(s) (${(res.cost_eur || 0).toFixed(2)} €)`);
    } catch (err) {
      if (err.message !== "PAGE_QUITTEE") toast.error(err.message || formatApiError(err.response?.data?.detail));
    } finally { if (mounted.current) { setPostImgBusy(null); setPostImgJob(null); } }
  };

  const selectedImage = (i) => {
    const k = postImgSel[i];
    return k == null ? null : (postImgResults[i]?.[k] ?? null);
  };

  const generate = async (ovType, ovInput) => {
    const stype = typeof ovType === "string" ? ovType : sourceType;
    const text = typeof ovInput === "string" ? ovInput : input;
    if (!text.trim()) { toast.error("Renseignez d'abord votre point de départ."); return; }
    setBusy(true);
    setProposals([]);
    try {
      const { data } = await api.post("/generate", { source_type: stype, source_input: text });
      await pollJob(data.job_id, (res) => {
        setThreadId(res.thread_id);
        setProposals(res.proposals);
        toast.success(`3 propositions générées (coût ≈ $${(res.cost_usd || 0).toFixed(4)})`);
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Pré-remplissage depuis un angle de veille (+ génération automatique en un clic)
  useEffect(() => {
    const pf = location.state?.prefill;
    if (pf && !prefillDone.current) {
      prefillDone.current = true;
      setSourceType(pf.sourceType || "link");
      setInput(pf.input || "");
      navigate(location.pathname, { replace: true, state: null });
      generate(pf.sourceType || "link", pf.input || "");
    }
  }, [location.state]); // eslint-disable-line

  const iterate = async (instruction) => {
    if (!threadId || !instruction.trim()) return;
    setIterating(true);
    try {
      const { data } = await api.post(`/generate/${threadId}/iterate`, { instruction });
      await pollJob(data.job_id, (res) => {
        setProposals(res.proposals);
        setCustomInstr("");
        toast.success("Propositions affinées.");
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setIterating(false);
    }
  };

  const updateProposal = (i, val) => {
    const arr = [...proposals];
    arr[i] = val;
    setProposals(arr);
  };

  const saveDraft = async (content, img, hasVisuals) => {
    try {
      const body = { content, status: "draft", source: sourceType };
      if (img) { body.image_data = img; body.image_mode = "post"; }
      await api.post("/posts", body);
      if (img) {
        toast.success("Brouillon enregistré avec son visuel dans « Mes posts ».");
      } else if (hasVisuals) {
        toast("Brouillon enregistré SANS visuel : cliquez d'abord sur un visuel (badge « Sélectionné »), le bouton devient « Brouillon + visuel ».", { duration: 8000 });
      } else {
        toast.success("Brouillon enregistré dans « Mes posts ».");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className={embedded ? "max-w-5xl" : "p-8 lg:p-12 max-w-5xl"}>
      {!embedded && <RecoveredVisuals />}
      {!embedded && (
        <div className="mb-2 flex items-center gap-2 text-coral-dark">
          <Sparkles className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em]">Génération</span>
        </div>
      )}
      <div className={`flex items-center gap-4 flex-wrap ${embedded ? "justify-end mb-6" : "justify-between mb-8"}`}>
        {!embedded && <h1 className="font-serif text-4xl text-slate-900">Créer un post</h1>}
        <button data-testid="from-veille-btn" onClick={() => navigate("/veille")}
          className="flex items-center gap-2 text-sm font-semibold text-coral-dark border border-coral px-4 py-2 rounded-sm hover:bg-coral-strong hover:text-white transition-colors">
          <Radar className="h-4 w-4" /> Partir d'un angle de ma veille
        </button>
      </div>

      {/* Source selector */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <div className="grid sm:grid-cols-3 gap-3 mb-5">
          {sources.map((s) => (
            <button key={s.key} data-testid={`source-${s.key}`} onClick={() => setSourceType(s.key)}
              className={`flex items-center gap-2.5 px-4 py-3 rounded-sm border text-sm font-medium transition-colors ${
                sourceType === s.key ? "border-coral bg-coral-strong text-white" : "border-slate-200 text-slate-600 hover:border-coral"
              }`}>
              <s.icon className="h-4 w-4" /> {s.label}
            </button>
          ))}
        </div>
        <textarea data-testid="generator-input" rows={4} value={input} onChange={(e) => setInput(e.target.value)}
          className="w-full border border-slate-200 rounded-sm px-3.5 py-3 text-sm outline-none focus:ring-1 focus:ring-coral focus:border-coral transition-colors resize-none"
          placeholder={activeSource.placeholder} />
        <button data-testid="generate-post-btn" onClick={generate} disabled={busy}
          className="mt-4 bg-coral-strong text-white px-6 py-3 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors disabled:opacity-60 flex items-center gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          {busy ? "Rédaction en cours…" : "Générer 3 propositions"}
        </button>
      </div>

      {/* Iteration bar */}
      {proposals.length > 0 && (
        <div className="bg-white/80 backdrop-blur-xl border border-slate-200/70 rounded-sm p-5 mb-8 shadow-[0_8px_32px_rgba(31,56,100,0.08)] sticky top-4 z-10">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-3">Affiner (itération)</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {quickActions.map((qa) => (
              <button key={qa} data-testid={`quick-${qa}`} disabled={iterating} onClick={() => iterate(qa)}
                className="px-3.5 py-2 rounded-full border border-slate-200 text-xs font-medium text-slate-600 hover:border-coral hover:text-coral-dark transition-colors disabled:opacity-50">
                {qa}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="custom-instruction-input" value={customInstr} onChange={(e) => setCustomInstr(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && iterate(customInstr)}
              className="flex-1 border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral focus:border-coral transition-colors"
              placeholder="Instruction libre : ex. « commence par une question »" />
            <button data-testid="custom-iterate-btn" disabled={iterating} onClick={() => iterate(customInstr)}
              className="bg-slate-900 text-white px-4 rounded-sm text-sm font-semibold hover:bg-slate-700 transition-colors disabled:opacity-60 flex items-center gap-2">
              {iterating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Affiner
            </button>
          </div>
        </div>
      )}

      {/* Proposals */}
      <div className="space-y-6">
        {proposals.map((p, i) => (
          <div key={i} data-testid={`proposal-${i}`} className="fade-in-up bg-white border border-slate-200 rounded-sm"
            style={{ animationDelay: `${i * 0.1}s` }}>
            <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-coral-dark">Proposition {i + 1}</span>
              <span className={`text-xs ${p.length > 3000 ? "font-bold text-red-600" : "text-slate-400"}`}>
                {p.length} / 3000 caractères{p.length > 3000 ? " : trop long pour LinkedIn" : ""}
              </span>
            </div>
            <textarea data-testid={`proposal-textarea-${i}`} value={p} onChange={(e) => updateProposal(i, e.target.value)}
              className="w-full px-6 py-5 font-serif text-[16px] leading-[1.7] text-slate-800 outline-none resize-none"
              style={{ minHeight: "180px" }} />
            <div className="flex flex-wrap items-center gap-3 px-6 py-4 border-t border-slate-100">
              <button data-testid={`save-draft-${i}`} onClick={() => saveDraft(p, selectedImage(i), (postImgResults[i]?.length || 0) > 0)}
                className="flex items-center gap-2 text-sm font-semibold text-coral-dark border border-coral px-4 py-2 rounded-sm hover:bg-coral-strong hover:text-white transition-colors">
                <Save className="h-4 w-4" /> Brouillon{selectedImage(i) ? " + visuel" : ""}
              </button>
              <button data-testid={`schedule-${i}`} onClick={() => {
                  const img = selectedImage(i);
                  setScheduleImage(img);
                  setScheduleMeta(img ? { image_mode: "post" } : null);
                  setScheduleFor(p);
                }}
                className="flex items-center gap-2 text-sm font-semibold text-white bg-coral-strong px-4 py-2 rounded-sm hover:bg-coral-hover transition-colors">
                <CalendarPlus className="h-4 w-4" /> Planifier{selectedImage(i) ? " + visuel" : ""}
              </button>
              <button data-testid={`post-image-${i}`} onClick={() => genPostImage(i, p)} disabled={postImgBusy === i}
                className="flex items-center gap-2 text-sm font-semibold text-white bg-slate-900 px-4 py-2 rounded-sm hover:bg-slate-700 transition-colors disabled:opacity-60">
                {postImgBusy === i ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Image du post
              </button>
              <label className="flex items-center gap-1.5 text-xs text-slate-500">
                M'inclure
                <select data-testid={`include-person-${i}`} value={includePerson}
                  onChange={(e) => setIncludePerson(e.target.value)}
                  className="border border-slate-200 rounded-sm px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-coral">
                  <option value="non">Non</option>
                  <option value="oui">Oui (mes photos)</option>
                  <option value="auto">Auto</option>
                </select>
              </label>
              <button data-testid={`visual-${i}`} onClick={() => setStudioFor(p)}
                className="flex items-center gap-2 text-sm font-semibold text-coral-dark border border-coral px-4 py-2 rounded-sm hover:bg-coral-strong hover:text-white transition-colors">
                <ImagePlus className="h-4 w-4" /> Personnaliser
              </button>
            </div>
            {postImgBusy === i && <ImageProgress job={postImgJob} testid={`post-image-progress-${i}`} />}
            {postImgResults[i]?.length > 0 && (
              <div className="px-6 pb-5">
                <p className="text-xs text-slate-500 mb-2">
                  Cliquez sur un visuel pour le sélectionner (« Voir en entier » : afficher en grand). Le visuel sélectionné
                  sera joint au brouillon ou à la planification.
                </p>
                <div className="grid grid-cols-2 gap-3 items-start">
                  {postImgResults[i].map((img, k) => (
                    <div key={k} className={`relative rounded-sm overflow-hidden border-2 transition-colors ${
                        postImgSel[i] === k ? "border-coral" : "border-slate-200 hover:border-coral/60"}`}>
                      <button data-testid={`post-image-result-${i}-${k}`}
                        onClick={() => setPostImgSel((prev) => ({ ...prev, [i]: prev[i] === k ? null : k }))}
                        className="block w-full">
                        <img src={img} alt={`visuel ${k + 1}`} className="w-full h-auto" />
                      </button>
                      <div className="flex items-stretch border-t border-slate-200 bg-white">
                        {postImgSel[i] === k && (
                          <span data-testid={`post-image-selected-${i}-${k}`} aria-label="Visuel sélectionné" title="Visuel sélectionné" className="flex items-center gap-1 px-2.5 text-[11px] font-bold text-coral-dark border-r border-slate-200">
                            <Check className="h-3.5 w-3.5" /><span className="hidden sm:inline">Sélectionné</span>
                          </span>
                        )}
                        <button title="Voir en entier" aria-label="Voir le visuel en entier" data-testid={`post-image-zoom-${i}-${k}`}
                          onClick={(e) => { e.stopPropagation(); setLightbox(img); }}
                          className="flex-1 flex items-center justify-center gap-1.5 text-slate-600 text-xs font-semibold py-1.5 hover:text-coral-dark hover:bg-slate-50 transition-colors">
                          <Maximize2 className="h-3.5 w-3.5" /><span className="hidden sm:inline">Voir en entier</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {scheduleFor !== null && (
        <ScheduleDialog
          content={scheduleFor}
          source={sourceType}
          initialImage={scheduleImage}
          initialImageMeta={scheduleMeta}
          onClose={() => { setScheduleFor(null); setScheduleImage(null); setScheduleMeta(null); }}
          onScheduled={() => { setScheduleFor(null); setScheduleImage(null); setScheduleMeta(null); toast.success("Post ajouté à la file de publication."); }}
        />
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="visuel en grand" className="max-h-[92vh] max-w-[92vw] object-contain rounded-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()} />
          <button aria-label="Fermer" onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white/90 hover:text-white">
            <X className="h-7 w-7" />
          </button>
        </div>
      )}

      {studioFor !== null && (
        <BrandTemplateStudio
          postContent={studioFor}
          profile={profile}
          authorNameDefault={user?.name}
          initialExpertPrompt={reusePrompt}
          onClose={() => { setStudioFor(null); setReusePrompt(null); }}
          onAttach={(dataUrl, meta) => {
            setScheduleImage(dataUrl);
            setScheduleMeta(meta || null);
            setScheduleFor(studioFor || "");
            setStudioFor(null);
            setReusePrompt(null);
            toast.success("Visuel attaché : planifiez ou publiez votre post.");
          }}
        />
      )}
    </div>
  );
}
